import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Modal from './Modal';
import LoadingButton from './LoadingButton';
import AcademicContextSelector from './AcademicContextSelector';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api';

const SubjectModal = ({
  isOpen,
  onClose,
  title,
  subject,
  setSubject,
  onSubmit,
  loading,
  actionText,
  editMode = false,
  tenantId,
  departmentId,
  token = null,
  initialAcademicYearId = '',
  initialSemesterId = null,
  initialTermId = null
}) => {
  const getTenantIdFromStorage = () => {
    try {
      const storedTenant = localStorage.getItem('tenantId') || localStorage.getItem('currentTenant');
      if (storedTenant) return storedTenant;

      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = JSON.parse(window.atob(parts[1]));
      return payload.tenantId || payload.tenant || null;
    } catch (err) {
      return null;
    }
  };

  const resolvedTenantId = tenantId || getTenantIdFromStorage();

  const getDepartmentIdFromStorage = () => {
    try {
      const raw = localStorage.getItem('departmentId') ||
        localStorage.getItem('currentDepartment') ||
        localStorage.getItem('currentDepartmentId');
      if (!raw) return null;

      try {
        const parsed = JSON.parse(raw);
        return parsed?.departmentId || parsed?._id || parsed?.id || null;
      } catch (e) {
        return raw;
      }
    } catch (err) {
      return null;
    }
  };

  const resolvedDepartmentId = departmentId ||
    (subject &&
      (subject.departmentId ||
        subject.department?._id ||
        subject.department?.id ||
        (typeof subject.department === 'string' ? subject.department : null))) ||
    getDepartmentIdFromStorage();

  const [institutionRules, setInstitutionRules] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [rulesFetchError, setRulesFetchError] = useState(null);

  // Academic context state
  const [academicContext, setAcademicContext] = useState({
    academicYearId: '',
    semesterId: null,
    termId: null,
    isSemesterBased: false
  });

  const [formData, setFormData] = useState({
    name: '',
    subjectCode: '',
    subjectCategory: 'academic',
    subjectType: 'theory',
    customSubjectType: '',
    isCreditBased: false,
    credits: '',
    requiresAttendance: true,
    appearsInTimetable: true,
    assessmentType: 'both',
    subjectManagementType: 'common',
    electiveType: '',
    languageType: '',
    requiresGroupManagement: false,
    maxMarks: '',
    cieMaxMarks: '',
    endExamMaxMarks: ''
  });

  const nameRef = useRef(null);
  const codeRef = useRef(null);
  const modalContentRef = useRef(null);
  const creditInputRef = useRef(null);
  
  // Track if initial setup is complete to prevent overwrites
  const initialSetupComplete = useRef(false);
  const subjectProcessed = useRef(false);

  // Helper to get user ID from storage
  const getUserIdFromStorage = useCallback(() => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken');
      if (!token) return null;
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      return payload.userId || payload.user_id || payload.sub || null;
    } catch (e) {
      return null;
    }
  }, []);


  // Reset everything when modal closes or subject changes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: '',
        subjectCode: '',
        subjectCategory: 'academic',
        subjectType: 'theory',
        customSubjectType: '',
        isCreditBased: false,
        credits: '',
        requiresAttendance: true,
        appearsInTimetable: true,
        assessmentType: 'both',
        subjectManagementType: 'common',
        electiveType: '',
        languageType: '',
        requiresGroupManagement: false,
        maxMarks: '',
        cieMaxMarks: '',
        endExamMaxMarks: ''
      });
      setValidationErrors([]);
      setAcademicContext({
        academicYearId: '',
        semesterId: null,
        termId: null,
        isSemesterBased: false
      });
      setInstitutionRules(null);
      setRulesFetchError(null);
      initialSetupComplete.current = false;
    }
    // Always reset subjectProcessed when modal opens or subject changes
    subjectProcessed.current = false;
  }, [isOpen, subject, editMode]);

  // Initialize form data from subject prop - ONLY ONCE when modal opens in edit mode
  useEffect(() => {
    if (isOpen && editMode && subject && Object.keys(subject).length > 0 && !subjectProcessed.current) {
      console.log('[EDIT MODE] Loaded subject:', subject);
      const loadedFormData = {
        name: subject.name || '',
        subjectCode: subject.subjectCode || '',
        subjectCategory: subject.subjectCategory || 'academic',
        subjectType: subject.subjectType || 'theory',
        customSubjectType: subject.customSubjectType || '',
        isCreditBased: subject.isCreditBased || false,
        credits: subject.credits || '',
        requiresAttendance: subject.requiresAttendance !== undefined ? subject.requiresAttendance : true,
        appearsInTimetable: subject.appearsInTimetable !== undefined ? subject.appearsInTimetable : true,
        assessmentType: subject.assessmentType || 'both',
        subjectManagementType: subject.subjectManagementType || 'common',
        electiveType: subject.electiveType || '',
        languageType: subject.languageType || '',
        requiresGroupManagement: subject.requiresGroupManagement || false,
        maxMarks: subject.maxMarks !== undefined && subject.maxMarks !== null ? subject.maxMarks : '',
        cieMaxMarks: subject.cieMaxMarks !== undefined && subject.cieMaxMarks !== null ? subject.cieMaxMarks : '',
        endExamMaxMarks: subject.endExamMaxMarks !== undefined && subject.endExamMaxMarks !== null ? subject.endExamMaxMarks : ''
      };
      console.log('[EDIT MODE] Setting formData:', loadedFormData);
      setFormData(loadedFormData);
      subjectProcessed.current = true;
    }
  }, [isOpen, editMode, subject]);

  // Initialize academic context - ONLY ONCE based on mode
  useEffect(() => {
    if (isOpen && !initialSetupComplete.current) {
      if (editMode && subject) {
        const extractId = (obj) => {
          if (!obj) return null;
          if (typeof obj === 'string') return obj;
          if (typeof obj === 'object') {
            if (obj.$oid) return obj.$oid;
            if (obj._id) {
              if (typeof obj._id === 'object' && obj._id.$oid) return obj._id.$oid;
              return obj._id;
            }
            if (obj.id) return obj.id;
          }
          return null;
        };

        setAcademicContext({
          academicYearId: extractId(subject.academicYearId) || initialAcademicYearId || '',
          semesterId: extractId(subject.semesterId) || initialSemesterId || null,
          termId: extractId(subject.termId) || initialTermId || null,
          isSemesterBased: !!(subject.semesterId || initialSemesterId)
        });
      } else {
        setAcademicContext({
          academicYearId: initialAcademicYearId || '',
          semesterId: initialSemesterId || null,
          termId: initialTermId || null,
          isSemesterBased: !!initialSemesterId
        });
      }
      initialSetupComplete.current = true;
    }
  }, [isOpen, editMode, subject, initialAcademicYearId, initialSemesterId, initialTermId]);

  const flattenErrors = useCallback((payload) => {
    const out = [];
    const seen = new Set();
    const walk = (v) => {
      if (!v || seen.has(v)) return;
      seen.add(v);
      if (typeof v === 'string') {
        out.push(v);
        return;
      }
      if (Array.isArray(v)) {
        v.forEach(walk);
        return;
      }
      if (typeof v === 'object') {
        if (typeof v.message === 'string') out.push(v.message);
        if (typeof v.msg === 'string') out.push(v.msg);
        if (Array.isArray(v.errors)) v.errors.forEach(walk);
        for (const key of Object.keys(v)) {
          const val = v[key];
          if (typeof val === 'string') out.push(val);
          else walk(val);
        }
      }
    };
    walk(payload);
    return out.filter(Boolean);
  }, []);

  const applyInstitutionDefaults = useCallback(
    (rules) => {
      if (!rules) return;

      setFormData((prev) => {
        const updates = {};
        const creditEnabled = !!(
          rules.academicSystem?.creditSystemEnabled &&
          (rules.academicSystem?.maxCredits ?? 0) > 0
        );
        const minCredits = rules.academicSystem?.minCredits ?? 0;

        if (creditEnabled) {
          if (!editMode && !subject) {
            updates.isCreditBased = true;
            updates.credits = minCredits;
          } else {
            if (prev.isCreditBased === undefined || prev.isCreditBased === null) {
              updates.isCreditBased = true;
            }
            if (prev.credits === undefined || prev.credits === null || prev.credits === '') {
              updates.credits = minCredits;
            }
          }
        } else {
          updates.isCreditBased = false;
          updates.credits = '';
        }

        if (rules.attendancePolicy) {
          if (!editMode && !subject) {
            updates.requiresAttendance = rules.attendancePolicy.enabled;
          } else if (prev.requiresAttendance === undefined || prev.requiresAttendance === null) {
            updates.requiresAttendance = rules.attendancePolicy.enabled;
          }
        }

        return { ...prev, ...updates };
      });
    },
    [editMode, subject]
  );

  const fetchInstitutionRules = useCallback(
    async (tid) => {
      setLoadingRules(true);
      setRulesFetchError(null);
      
      try {
        const endpoint = `/api/grading-attendance-rules/${tid}`;
        
        const config = {};
        if (token) {
          config.headers = {
            'Authorization': `Bearer ${token}`
          };
        }
        
        const response = await api.get(endpoint, config);
        
        if (response.data?.success && response.data?.rules) {
          setInstitutionRules(response.data.rules);
          applyInstitutionDefaults(response.data.rules);
        } else if (response.data?.rules) {
          setInstitutionRules(response.data.rules);
          applyInstitutionDefaults(response.data.rules);
        } else {
          throw new Error('Invalid response structure');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch institution rules';
        setRulesFetchError(errorMessage);
        setInstitutionRules(null);
        
        try {
          toast.warning('Could not fetch institution rules. Using default configuration.');
        } catch (tErr) {
          // Silently fail
        }
      } finally {
        setLoadingRules(false);
      }
    },
    [applyInstitutionDefaults, token]
  );

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      const msgs = flattenErrors(detail);
      if (msgs.length === 0) return;
      setValidationErrors(msgs);

      try {
        msgs.forEach((m) => {
          if (typeof m === 'string' && m.trim()) toast.error(m);
        });
      } catch (tErr) {
        // Silently fail
      }

      const joined = msgs.join(' ').toLowerCase();
      const isCodeError =
        joined.includes('subject code already exists') ||
        joined.includes('code already exists') ||
        joined.includes('duplicate code') ||
        joined.includes('subject code');

      setTimeout(() => {
        const targetRef = isCodeError ? codeRef : nameRef;
        if (targetRef?.current) {
          try {
            targetRef.current.focus();
            if (typeof targetRef.current.scrollIntoView === 'function') {
              targetRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          } catch (err) { }
        }
        if (modalContentRef?.current)
          modalContentRef.current.scrollTop = 0;
      }, 50);
    };

    window.addEventListener('subjectManagerError', handler);
    window.addEventListener('subjectErrors', handler);
    return () => {
      window.removeEventListener('subjectManagerError', handler);
      window.removeEventListener('subjectErrors', handler);
    };
  }, [flattenErrors]);

  useEffect(() => {
    if (isOpen && resolvedTenantId) {
      fetchInstitutionRules(resolvedTenantId);
    }
  }, [isOpen, resolvedTenantId, fetchInstitutionRules]);

  const isCreditSystemEnabled = useCallback(() => {
    if (!institutionRules) {
      return false;
    }
    
    return !!(
      institutionRules.academicSystem?.creditSystemEnabled &&
      (institutionRules.academicSystem?.maxCredits ?? 0) > 0
    );
  }, [institutionRules]);

  const applySubjectTypeRules = (subjectType) => {
    const rules = {
      theory: {
        assessmentType: 'marks',
        isCreditBased: isCreditSystemEnabled()
      },
      lab: {
        assessmentType: 'marks',
        isCreditBased: isCreditSystemEnabled()
      },
      elective: {
        assessmentType: 'marks',
        isCreditBased: isCreditSystemEnabled()
      },
      language: {
        assessmentType: 'marks',
        isCreditBased: isCreditSystemEnabled()
      },
      project: {
        assessmentType: 'marks',
        isCreditBased: formData.subjectCategory !== 'non_academic' && isCreditSystemEnabled()
      },
      ncc: {
        assessmentType: 'participation',
        isCreditBased: false
      },
      nss: {
        assessmentType: 'participation',
        isCreditBased: false
      },
      sports: {
        assessmentType: 'participation',
        isCreditBased: formData.subjectCategory === 'academic' && isCreditSystemEnabled()
      },
      cultural: {
        assessmentType: 'participation',
        isCreditBased: false
      }
    };

    return rules[subjectType] || {};
  };

  const handleCategoryChange = (category) => {
    const updates = { subjectCategory: category };

    if (category === 'non_academic') {
      updates.isCreditBased = false;
      updates.credits = '';
      updates.subjectManagementType = 'common';
      updates.requiresGroupManagement = false;

      if (formData.name.toLowerCase().includes('ncc')) {
        updates.subjectType = 'ncc';
      } else if (formData.name.toLowerCase().includes('nss')) {
        updates.subjectType = 'nss';
      } else if (formData.name.toLowerCase().includes('sports')) {
        updates.subjectType = 'sports';
      } else {
        updates.subjectType = 'cultural';
      }
    } else if (category === 'academic') {
      if (formData.requiresAttendance === undefined) {
        updates.requiresAttendance = true;
      }
      if (formData.appearsInTimetable === undefined) {
        updates.appearsInTimetable = true;
      }
      updates.isCreditBased = isCreditSystemEnabled();

      if (updates.isCreditBased) {
        updates.credits = institutionRules?.academicSystem?.minCredits || 3;
      }

      if (formData.subjectCategory === 'non_academic') {
        updates.subjectType = 'theory';
        updates.subjectManagementType = 'common';
        updates.requiresGroupManagement = false;
      }
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubjectTypeChange = (subjectType) => {
    const typeRules = applySubjectTypeRules(subjectType);

    const isElectiveOrLanguage = subjectType === 'elective' || subjectType === 'language';

    const managementUpdates = {};
    if (isElectiveOrLanguage) {
      managementUpdates.subjectManagementType = formData.subjectManagementType || 'common';
      managementUpdates.requiresGroupManagement = formData.subjectManagementType === 'choice-based';
    } else {
      managementUpdates.subjectManagementType = 'common';
      managementUpdates.requiresGroupManagement = false;
      managementUpdates.electiveType = '';
      managementUpdates.languageType = '';
    }

    const updates = {
      subjectType,
      ...typeRules,
      ...managementUpdates
    };
    if (formData.requiresAttendance !== undefined) {
      updates.requiresAttendance = formData.requiresAttendance;
    }
    if (formData.appearsInTimetable !== undefined) {
      updates.appearsInTimetable = formData.appearsInTimetable;
    }
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleManagementTypeChange = (managementType) => {
    const updates = {
      subjectManagementType: managementType,
      requiresGroupManagement: managementType === 'choice-based'
    };

    if (formData.subjectType === 'elective') {
      updates.electiveType = managementType === 'choice-based' ? 'choice-based' : 'common';
      updates.languageType = '';
    } else if (formData.subjectType === 'language') {
      updates.languageType = managementType === 'choice-based' ? 'choice-based' : 'common';
      updates.electiveType = '';
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Calculate CIE and External weightage dynamically
  const calculateWeightages = useCallback(() => {
    const maxMarks = Number(formData.maxMarks) || 0;
    const cie = Number(formData.cieMaxMarks) || 0;
    const external = Number(formData.endExamMaxMarks) || 0;
    let cieWeightage = 0;
    let externalWeightage = 0;
    if (maxMarks > 0) {
      if (formData.assessmentType === 'internal_only') {
        cieWeightage = 100;
        externalWeightage = 0;
      } else if (formData.assessmentType === 'external_only') {
        cieWeightage = 0;
        externalWeightage = 100;
      } else {
        cieWeightage = Math.round((cie / maxMarks) * 1000) / 10;
        externalWeightage = Math.round((external / maxMarks) * 1000) / 10;
      }
    }
    return { cieWeightage, externalWeightage };
  }, [formData.maxMarks, formData.cieMaxMarks, formData.endExamMaxMarks, formData.assessmentType]);

  const { cieWeightage, externalWeightage } = useMemo(() => calculateWeightages(), [calculateWeightages]);

  const handleInputChange = (field, value) => {
    setValidationErrors([]);
    // Convert to number if not blank, else keep as blank
    let maxMarksNum = formData.maxMarks === '' ? '' : Number(formData.maxMarks);
    if (field === 'maxMarks') {
      maxMarksNum = value === '' ? '' : Number(value);
    }

    // Auto-calculate CIE/End Exam marks when assessment type changes
    if (field === 'assessmentType') {
      if (maxMarksNum === '' || isNaN(maxMarksNum) || maxMarksNum <= 0) {
        setFormData((prev) => ({ ...prev, assessmentType: value }));
        return;
      }
      if (value === 'internal_only') {
        setFormData((prev) => ({
          ...prev,
          assessmentType: value,
          cieMaxMarks: maxMarksNum,
          endExamMaxMarks: 0
        }));
        return;
      } else if (value === 'external_only') {
        setFormData((prev) => ({
          ...prev,
          assessmentType: value,
          cieMaxMarks: 0,
          endExamMaxMarks: maxMarksNum
        }));
        return;
      } else if (value === 'both' && maxMarksNum > 0) {
        const halfMarks = Math.floor(maxMarksNum / 2);
        setFormData((prev) => ({
          ...prev,
          assessmentType: value,
          cieMaxMarks: halfMarks,
          endExamMaxMarks: maxMarksNum - halfMarks
        }));
        return;
      }
    }

    // Auto-calculate when maxMarks changes
    if (field === 'maxMarks') {
      if (value === '' || isNaN(Number(value)) || Number(value) <= 0) {
        setFormData((prev) => ({ ...prev, maxMarks: '' }));
        return;
      }
      const newMax = Number(value);
      if (formData.assessmentType === 'internal_only') {
        setFormData((prev) => ({
          ...prev,
          maxMarks: newMax,
          cieMaxMarks: newMax,
          endExamMaxMarks: 0
        }));
        return;
      } else if (formData.assessmentType === 'external_only') {
        setFormData((prev) => ({
          ...prev,
          maxMarks: newMax,
          cieMaxMarks: 0,
          endExamMaxMarks: newMax
        }));
        return;
      } else if (formData.assessmentType === 'both' && newMax > 0) {
        // If user just changed maxMarks, keep CIE as is if possible, else split
        let cie = Number(formData.cieMaxMarks) || 0;
        let endExam = Number(formData.endExamMaxMarks) || 0;
        if (cie + endExam !== newMax) {
          // Split evenly if previous sum doesn't match
          cie = Math.floor(newMax / 2);
          endExam = newMax - cie;
        }
        setFormData((prev) => ({
          ...prev,
          maxMarks: newMax,
          cieMaxMarks: cie,
          endExamMaxMarks: endExam
        }));
        return;
      } else {
        setFormData((prev) => ({ ...prev, maxMarks: newMax }));
        return;
      }
    }

    // For "both", auto-adjust the other field so sum is always maxMarks
    if (formData.assessmentType === 'both' && maxMarksNum > 0) {
      if (field === 'cieMaxMarks') {
        let cie = Number(value) || 0;
        if (cie > maxMarksNum) cie = maxMarksNum;
        setFormData((prev) => ({
          ...prev,
          cieMaxMarks: cie,
          endExamMaxMarks: maxMarksNum - cie
        }));
        return;
      } else if (field === 'endExamMaxMarks') {
        let endExam = Number(value) || 0;
        if (endExam > maxMarksNum) endExam = maxMarksNum;
        setFormData((prev) => ({
          ...prev,
          endExamMaxMarks: endExam,
          cieMaxMarks: maxMarksNum - endExam
        }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAcademicContextChange = (context) => {
    setAcademicContext({
      academicYearId: context.academicYearId,
      semesterId: context.semesterId,
      termId: context.termId,
      isSemesterBased: context.isSemesterBased
    });
  };

  const validateAgainstInstitutionRules = () => {
    const errors = [];
    if (!institutionRules) return errors;

    if (isCreditSystemEnabled() && formData.isCreditBased) {
      const minCredits = institutionRules.academicSystem?.minCredits ?? 0;
      const maxCredits = institutionRules.academicSystem?.maxCredits ?? 999;
      
      if (formData.credits === '' || formData.credits === null || formData.credits === undefined) {
        errors.push('Please enter credit value');
      } else {
        const numeric = typeof formData.credits === 'number' && !Number.isNaN(formData.credits);
        if (!numeric) {
          errors.push('Credit value must be a number');
        } else {
          if (formData.credits < minCredits || formData.credits > maxCredits) {
            errors.push(`Credits must be between ${minCredits} and ${maxCredits}`);
          }
          if (!institutionRules.academicSystem?.allowDecimalCredits && formData.credits % 1 !== 0) {
            errors.push('Decimal credits are not allowed');
          }
        }
      }
    }

    if (formData.requiresAttendance && !institutionRules.attendancePolicy?.enabled) {
      errors.push('Attendance tracking is disabled at institution level');
    }

    return errors;
  };

  const handleSubmit = async () => {

    setValidationErrors([]);
    setValidating(true);

    const localErrors = [];

    console.log('[DEBUG] handleSubmit - formData.maxMarks:', formData.maxMarks, 'typeof:', typeof formData.maxMarks);
    if (!formData.name || !formData.name.trim())
      localErrors.push('Please enter subject name');
    if (!formData.subjectCode || !formData.subjectCode.trim())
      localErrors.push('Please enter subject code');
    if (!formData.assessmentType || !['internal_only', 'external_only', 'both'].includes(formData.assessmentType)) {
      localErrors.push('Please select an assessment type');
    }
    // MARKS VALIDATION
    if (!formData.maxMarks || isNaN(Number(formData.maxMarks)) || Number(formData.maxMarks) <= 0) {
      console.log('[DEBUG] Validation failed for maxMarks:', formData.maxMarks, 'typeof:', typeof formData.maxMarks);
      localErrors.push('Please enter a valid Maximum Marks');
    }

    const maxMarksNum = Number(formData.maxMarks);
    let cieMax = Number(formData.cieMaxMarks) || 0;
    let endExamMax = Number(formData.endExamMaxMarks) || 0;

    console.log('[DEBUG] maxMarksNum:', maxMarksNum, 'cieMax:', cieMax, 'endExamMax:', endExamMax);

    if (formData.assessmentType === 'internal_only') {
      cieMax = maxMarksNum;
      endExamMax = 0;
    } else if (formData.assessmentType === 'external_only') {
      cieMax = 0;
      endExamMax = maxMarksNum;
    } else if (formData.assessmentType === 'both') {
      if (!cieMax && !endExamMax) {
        cieMax = Math.floor(maxMarksNum / 2);
        endExamMax = maxMarksNum - cieMax;
      }
      if ((cieMax + endExamMax) !== maxMarksNum) {
        console.log('[DEBUG] CIE + EndExam != maxMarksNum:', cieMax, '+', endExamMax, '!=', maxMarksNum);
        localErrors.push('Sum of CIE and End Exam marks must equal Maximum Marks');
      }
    }

    const isElectiveOrLanguage = formData.subjectType === 'elective' || formData.subjectType === 'language';
    if (isElectiveOrLanguage && !formData.subjectManagementType) {
      localErrors.push('Please select subject management type for elective or language subjects.');
    }

    if (!academicContext.academicYearId)
      localErrors.push('Please select an academic year');

    if (academicContext.isSemesterBased) {
      if (!academicContext.semesterId) {
        localErrors.push('Please select a semester');
      }
    } else {
      if (!academicContext.termId) {
        localErrors.push('Please select a term');
      }
    }

    const finalDeptId = (() => {
      if (!resolvedDepartmentId) return null;
      if (typeof resolvedDepartmentId === 'string')
        return resolvedDepartmentId;
      if (typeof resolvedDepartmentId === 'object') {
        return resolvedDepartmentId.departmentId ||
          resolvedDepartmentId._id ||
          resolvedDepartmentId.id ||
          null;
      }
      return null;
    })();
    if (!finalDeptId)
      localErrors.push('Please select a department before creating a subject');

    if (isCreditSystemEnabled() && formData.isCreditBased) {
      const minC = institutionRules?.academicSystem?.minCredits ?? 0;
      const maxC = institutionRules?.academicSystem?.maxCredits ?? 999;

      if (formData.credits === '' || formData.credits === null || formData.credits === undefined) {
        localErrors.push('Please enter credit value');
      } else {
        const numeric = typeof formData.credits === 'number' && !Number.isNaN(formData.credits);
        if (!numeric) {
          localErrors.push('Please enter a valid credit value');
        } else if (formData.credits < minC || formData.credits > maxC) {
          localErrors.push(`Credits must be between ${minC} and ${maxC}`);
        } else if (!institutionRules?.academicSystem?.allowDecimalCredits && formData.credits % 1 !== 0) {
          localErrors.push('Decimal credits are not allowed');
        }
      }
    }

    const institutionErrors = validateAgainstInstitutionRules();
    const allErrors = [...localErrors, ...institutionErrors];
    if (allErrors.length > 0) {
      setValidationErrors(allErrors);
      setValidating(false);
      setTimeout(() => {
        if (nameRef.current) nameRef.current.focus();
      }, 50);
      return;
    }

    // Get createdBy from storage
    const createdBy = getUserIdFromStorage();

    const submitData = {
      name: formData.name.trim(),
      subjectCode: formData.subjectCode.trim().toUpperCase(),
      subjectCategory: formData.subjectCategory,
      subjectType: formData.subjectType,
      customSubjectType: formData.subjectType === 'custom' ? formData.customSubjectType || '' : '',
      isCreditBased: isCreditSystemEnabled() ? formData.isCreditBased : false,
      credits: isCreditSystemEnabled() && formData.isCreditBased ? Number(formData.credits) : 0,
      requiresAttendance: formData.requiresAttendance,
      appearsInTimetable: formData.appearsInTimetable,
      assessmentType: formData.assessmentType,
      subjectManagementType: isElectiveOrLanguage ? formData.subjectManagementType : 'common',
      electiveType: formData.electiveType || '',
      languageType: formData.languageType || '',
      requiresGroupManagement: isElectiveOrLanguage ? formData.requiresGroupManagement : false,
      isLab: formData.subjectType === 'lab',
      isElective: formData.subjectType === 'elective',
      isLanguage: formData.subjectType === 'language',
      isProject: ['project', 'ncc', 'nss', 'sports', 'cultural'].includes(formData.subjectType),
      tenantId: resolvedTenantId,
      departmentId: finalDeptId,
      academicYearId: academicContext.academicYearId,
      semesterId: academicContext.semesterId || null,
      termId: academicContext.termId || null,
      isSemesterBased: academicContext.isSemesterBased,
      createdBy: createdBy,
      maxMarks: maxMarksNum > 0 ? maxMarksNum : undefined,
      cieMaxMarks: Number(cieMax),
      endExamMaxMarks: Number(endExamMax)
    };

    console.log('[SubjectModal] Sending submitData:', submitData);
    console.log('[DEBUG] maxMarks in submitData:', submitData.maxMarks, 'typeof:', typeof submitData.maxMarks);

    if (setSubject) {
      setSubject(submitData);
    }

    if (typeof onSubmit === 'function') {
      try {
        await onSubmit(submitData);
        try {
          toast.success(editMode ? 'Subject updated successfully' : 'Subject created successfully');
        } catch (tErr) {
          // Silently fail
        }
        onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const payload = err?.response?.data ?? err;
        const messages = flattenErrors(payload);
        if (messages.length === 0) {
          const top = (err && (err.message || err?.response?.statusText)) || 'Error saving subject';
          setValidationErrors([top]);
          try {
            toast.error(top);
          } catch { }
        } else {
          setValidationErrors(messages);
          try {
            messages.forEach((m) => m && toast.error(m));
          } catch { }
        }
        const joined = (messages.join(' ') || '').toLowerCase();
        const isCodeError = joined.includes('subject code already exists') ||
          joined.includes('code already exists') ||
          joined.includes('duplicate code') ||
          joined.includes('subject code');
        setTimeout(() => {
          const targetRef = isCodeError ? codeRef : nameRef;
          if (targetRef?.current) {
            try {
              targetRef.current.focus();
              if (modalContentRef?.current &&
                typeof targetRef.current.scrollIntoView === 'function') {
                targetRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
            } catch { }
          }
        }, 50);
      } finally {
        setValidating(false);
      }
    } else {
      setValidating(false);
    }
  };

  const getCategoryOptions = () => [
    {
      value: 'academic',
      label: 'Academic Subject',
      description: 'Regular theory, lab, elective subjects',
      icon: '📚'
    },
    {
      value: 'non_academic',
      label: 'Non-Academic Activity',
      description: 'NCC, NSS, Sports, Cultural activities',
      icon: '🎯'
    }
  ];

  const getSubjectTypeOptions = () => {
    const baseOptions = [
      { value: 'theory', label: 'Core Theory', icon: '📚', description: 'Traditional classroom teaching' },
      { value: 'lab', label: 'Laboratory', icon: '🔬', description: 'Practical lab sessions' },
      { value: 'elective', label: 'Elective', icon: '📖', description: 'Student choice subjects' },
      { value: 'language', label: 'Language', icon: '🗣️', description: 'Language learning' },
      { value: 'project', label: 'Project Work', icon: '📋', description: 'Project-based learning' },
      { value: 'custom', label: 'Custom', icon: '✏️', description: 'Define your own type' }
    ];

    if (formData.subjectCategory === 'non_academic') {
      return [
        { value: 'ncc', label: 'NCC', icon: '🎖️', description: 'National Cadet Corps' },
        { value: 'nss', label: 'NSS', icon: '🤝', description: 'National Service Scheme' },
        { value: 'sports', label: 'Sports', icon: '⚽', description: 'Sports activities' },
        { value: 'cultural', label: 'Cultural', icon: '🎭', description: 'Cultural activities' },
        { value: 'custom', label: 'Custom', icon: '✏️', description: 'Define your own type' }
      ];
    }

    return baseOptions;
  };

  const getManagementTypeOptions = () => {
    if (formData.subjectType !== 'elective' && formData.subjectType !== 'language') {
      return [];
    }

    return [
      {
        value: 'common',
        label: 'Common Subject',
        description: 'Available to all students without grouping',
        icon: '👥'
      },
      {
        value: 'choice-based',
        label: 'Choice-Based Subject',
        description: 'Students choose from groups of subjects',
        icon: '🎯'
      }
    ];
  };

  const isFormValid = () => {
    const basicValid = formData.name.trim() &&
      formData.subjectCode.trim() &&
      academicContext.academicYearId &&
      validationErrors.length === 0 &&
      formData.maxMarks &&
      parseFloat(formData.maxMarks) > 0;

    if (academicContext.isSemesterBased) {
      if (!academicContext.semesterId) return false;
    } else {
      if (!academicContext.termId) return false;
    }

    if ((formData.subjectType === 'elective' || formData.subjectType === 'language') &&
      !formData.subjectManagementType) {
      return false;
    }

    return basicValid;
  };

  if (!isOpen) return null;

  const showCreditInput = isCreditSystemEnabled();
  const minCredits = institutionRules?.academicSystem?.minCredits ?? 0;
  const maxCredits = institutionRules?.academicSystem?.maxCredits ?? 999;
  const allowDecimalCredits = institutionRules?.academicSystem?.allowDecimalCredits ?? false;

  return (
    <Modal title={title} onClose={onClose} loading={loading} size="3xl">
      <div className="flex flex-col h-[70vh]">
        <div ref={modalContentRef} className="flex-1 overflow-y-auto px-1 py-2 space-y-6">
          {/* Error Section */}
          {validationErrors.length > 0 && (
            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-red-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 text-sm mb-2">
                    Please fix the following issues:
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Rules Fetch Error Warning */}
          {rulesFetchError && !institutionRules && (
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-yellow-200 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 text-sm mb-1">
                    Warning: Institution Rules Not Available
                  </h4>
                  <p className="text-sm text-yellow-700">
                    {rulesFetchError} Using default configuration.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Academic Context Section */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Academic Context</h3>
              <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                Required
              </span>
            </div>
            <AcademicContextSelector
              tenantId={resolvedTenantId}
              departmentId={resolvedDepartmentId}
              onContextChange={handleAcademicContextChange}
              initialAcademicYearId={academicContext.academicYearId}
              initialSemesterId={academicContext.semesterId}
              initialTermId={academicContext.termId}
              showLabels={true}
              compact={false}
              token={token}
              key={`academic-context-${isOpen ? 'open' : 'closed'}`}
            />
          </div>

          {/* Institution Rules Summary */}
          {loadingRules ? (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-blue-200 rounded w-1/4"></div>
                  <div className="h-3 bg-blue-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ) : institutionRules && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 text-base mb-3">
                    Institution Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/50 rounded-lg p-3">
                      <div className="text-xs font-medium text-blue-700 mb-1">Academic System</div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          institutionRules.academicSystem?.systemType === 'semester' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {institutionRules.academicSystem?.systemType?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white/50 rounded-lg p-3">
                      <div className="text-xs font-medium text-blue-700 mb-1">Credit System</div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          isCreditSystemEnabled() 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {isCreditSystemEnabled() ? 'Enabled' : 'Disabled'}
                        </span>
                        {isCreditSystemEnabled() && (
                          <span className="text-xs text-gray-600">
                            ({minCredits}-{maxCredits} credits)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white/50 rounded-lg p-3">
                      <div className="text-xs font-medium text-blue-700 mb-1">Attendance</div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          institutionRules.attendancePolicy?.enabled 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {institutionRules.attendancePolicy?.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {institutionRules.attendancePolicy?.enabled && (
                          <span className="text-xs text-gray-600">
                            (Min {institutionRules.attendancePolicy?.defaultMinimumPercentage}%)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-green-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Subject Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  ref={nameRef}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Mathematics, NCC Training"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-400"
                  required
                />
                <p className="text-xs text-gray-500">Enter the full name of the subject</p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Subject Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  ref={codeRef}
                  value={formData.subjectCode}
                  onChange={(e) => handleInputChange('subjectCode', e.target.value.toUpperCase())}
                  placeholder="e.g., MATH101"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-400 uppercase"
                  required
                />
                <p className="text-xs text-gray-500">Unique identifier for the subject</p>
              </div>
            </div>
          </div>

          {/* Assessment Type & Marks Configuration Combined */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-pink-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Assessment & Marks Configuration</h3>
              <span className="ml-auto text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full">Required</span>
            </div>
            
            {/* Assessment Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Assessment Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'internal_only', label: 'Internal Only', description: '100% weightage to CIE' },
                  { value: 'external_only', label: 'External Only', description: '100% weightage to End Exam' },
                  { value: 'both', label: 'Both (Internal + External)', description: 'Split between CIE and End Exam' }
                ].map((option) => (
                  <label key={option.value} className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.assessmentType === option.value ? 'border-pink-500 bg-pink-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="assessmentType"
                      value={option.value}
                      checked={formData.assessmentType === option.value}
                      onChange={() => handleInputChange('assessmentType', option.value)}
                      className="form-radio h-5 w-5 text-pink-600 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Marks Configuration */}
            <div className="border-t border-gray-200 pt-6">
              {/* Weightage Display */}
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col items-center">
                  <div className="font-semibold text-blue-900">CIE Weightage</div>
                  <div className="text-2xl font-bold text-blue-700">{cieWeightage}%</div>
                </div>
                <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 flex flex-col items-center">
                  <div className="font-semibold text-pink-900">External Weightage</div>
                  <div className="text-2xl font-bold text-pink-700">{externalWeightage}%</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Maximum Marks <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.maxMarks}
                    onChange={e => handleInputChange('maxMarks', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all hover:border-gray-400"
                    placeholder="e.g., 100"
                    required
                  />
                  <p className="text-xs text-gray-500">Total/Maximum marks for this subject</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    CIE Max Marks {formData.assessmentType === 'both' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.cieMaxMarks}
                    onChange={e => handleInputChange('cieMaxMarks', e.target.value)}
                    disabled={formData.assessmentType === 'external_only'}
                    className={`w-full px-4 py-3 border rounded-xl transition-all hover:border-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      formData.assessmentType === 'external_only' 
                        ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder={formData.assessmentType === 'both' ? "e.g., 50" : "Auto-calculated"}
                  />
                  <p className="text-xs text-gray-500">
                    {formData.assessmentType === 'internal_only' && '100% weightage for CIE'}
                    {formData.assessmentType === 'external_only' && 'Not applicable for External Only'}
                    {formData.assessmentType === 'both' && 'Max marks for CIE (internal assessment)'}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    End Exam Max Marks {formData.assessmentType === 'both' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.endExamMaxMarks}
                    onChange={e => handleInputChange('endExamMaxMarks', e.target.value)}
                    disabled={formData.assessmentType === 'internal_only'}
                    className={`w-full px-4 py-3 border rounded-xl transition-all hover:border-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      formData.assessmentType === 'internal_only' 
                        ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder={formData.assessmentType === 'both' ? "e.g., 50" : "Auto-calculated"}
                  />
                  <p className="text-xs text-gray-500">
                    {formData.assessmentType === 'internal_only' && 'Not applicable for Internal Only'}
                    {formData.assessmentType === 'external_only' && '100% weightage for End Exam'}
                    {formData.assessmentType === 'both' && 'Max marks for End Exam (external assessment)'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Credit Configuration - Only shown when credit system is enabled */}
          {showCreditInput && institutionRules && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
                <h3 className="text-lg font-semibold text-gray-900">Credit Configuration</h3>
                <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                  Required
                </span>
              </div>
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Credit Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    ref={creditInputRef}
                    step={allowDecimalCredits ? 0.5 : 1}
                    value={formData.credits}
                    onChange={(e) => handleInputChange('credits', e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all hover:border-gray-400"
                    placeholder={`Enter credit value (${minCredits}-${maxCredits})`}
                  />
                  <div className="flex items-center gap-4 text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 4a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Min: {minCredits}
                    </span>
                    <span className="inline-flex items-center gap-1 text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 4a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Max: {maxCredits}
                    </span>
                    {allowDecimalCredits && (
                      <span className="inline-flex items-center gap-1 text-purple-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                        </svg>
                        Decimals allowed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subject Category */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-orange-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Subject Category</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {getCategoryOptions().map((category) => (
                <div
                  key={category.value}
                  onClick={() => handleCategoryChange(category.value)}
                  className={`border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 ${
                    formData.subjectCategory === category.value
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-6 h-6 border-2 rounded-full flex items-center justify-center mt-0.5 ${
                      formData.subjectCategory === category.value
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-400'
                    }`}>
                      {formData.subjectCategory === category.value && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{category.icon}</span>
                        <div className="font-semibold text-gray-900">
                          {category.label}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subject Type */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-indigo-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Subject Type</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {getSubjectTypeOptions().map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleSubjectTypeChange(type.value)}
                  className={`flex flex-col items-center p-4 border-2 rounded-xl transition-all duration-200 ${
                    formData.subjectType === type.value
                      ? 'border-indigo-500 bg-indigo-50 shadow-md scale-105'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm hover:scale-102'
                  }`}
                >
                  <div className="text-2xl mb-2">{type.icon}</div>
                  <div className="text-xs font-semibold text-gray-900 text-center">
                    {type.label}
                  </div>
                  {type.description && (
                    <div className="text-[10px] text-gray-500 text-center mt-1 hidden lg:block">
                      {type.description}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {formData.subjectType === 'custom' && (
              <div className="mt-6 space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Custom Subject Type
                </label>
                <input
                  type="text"
                  value={formData.customSubjectType}
                  onChange={(e) => handleInputChange('customSubjectType', e.target.value)}
                  placeholder="Enter custom subject label"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
                <p className="text-xs text-gray-500">Define your own subject type label</p>
              </div>
            )}
          </div>

          {/* Subject Management Type - Only for Elective/Language */}
          {(formData.subjectType === 'elective' || formData.subjectType === 'language') && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
                <h3 className="text-lg font-semibold text-gray-900">Subject Management</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {getManagementTypeOptions().map((managementType) => (
                  <div
                    key={managementType.value}
                    onClick={() => handleManagementTypeChange(managementType.value)}
                    className={`border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 ${
                      formData.subjectManagementType === managementType.value
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-6 h-6 border-2 rounded-full flex items-center justify-center mt-0.5 ${
                        formData.subjectManagementType === managementType.value
                          ? 'border-purple-500 bg-purple-500'
                          : 'border-gray-400'
                      }`}>
                        {formData.subjectManagementType === managementType.value && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{managementType.icon}</span>
                          <div className="font-semibold text-gray-900">
                            {managementType.label}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {managementType.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {formData.subjectManagementType === 'choice-based' && (
                <div className="mt-4 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-900 text-sm">
                        Choice-Based Configuration
                      </h4>
                      <p className="text-xs text-yellow-700 mt-1">
                        This subject will require group management. Students will choose from groups of subjects.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subject Settings */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-teal-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">Subject Settings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Track Attendance</div>
                    <div className="text-xs text-gray-600">
                      Monitor student attendance for this subject
                    </div>
                  </div>
                </div>
                <div className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  formData.requiresAttendance ? 'bg-green-600' : 'bg-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.requiresAttendance}
                    onChange={(e) => handleInputChange('requiresAttendance', e.target.checked)}
                    disabled={!institutionRules?.attendancePolicy?.enabled}
                    className="sr-only"
                  />
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    formData.requiresAttendance ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>

              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Appear in Timetable</div>
                    <div className="text-xs text-gray-600">
                      Include this subject in class schedules
                    </div>
                  </div>
                </div>
                <div className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  formData.appearsInTimetable ? 'bg-purple-600' : 'bg-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.appearsInTimetable}
                    onChange={(e) => handleInputChange('appearsInTimetable', e.target.checked)}
                    className="sr-only"
                  />
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    formData.appearsInTimetable ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
              </label>
            </div>
          </div>

          {/* Configuration Summary */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-6 bg-gray-600 rounded-full"></div>
              <h4 className="text-lg font-semibold text-gray-900">Configuration Summary</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Academic Year
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    academicContext.academicYearId ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  <span className="font-semibold text-gray-900 text-sm">
                    {academicContext.academicYearId ? 'Selected' : 'Not selected'}
                  </span>
                </div>
              </div>
              {academicContext.isSemesterBased ? (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Semester
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      academicContext.semesterId ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    <span className="font-semibold text-gray-900 text-sm">
                      {academicContext.semesterId ? 'Selected' : 'Not selected'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Term
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      academicContext.termId ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    <span className="font-semibold text-gray-900 text-sm">
                      {academicContext.termId ? 'Selected' : 'Not selected'}
                    </span>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Category
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    formData.subjectCategory === 'academic' ? 'bg-blue-500' : 'bg-orange-500'
                  }`}></span>
                  <span className="font-semibold text-gray-900 text-sm">
                    {formData.subjectCategory === 'academic' ? 'Academic' : 'Non-Academic'}
                  </span>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Type
                </div>
                <span className="font-semibold text-gray-900 text-sm">
                  {getSubjectTypeOptions().find((t) => t.value === formData.subjectType)?.label}
                </span>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Assessment
                </div>
                <span className="font-semibold text-gray-900 text-sm">
                  {formData.assessmentType === 'internal_only' && 'Internal Only'}
                  {formData.assessmentType === 'external_only' && 'External Only'}
                  {formData.assessmentType === 'both' && 'Both'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 border-t bg-white px-6 py-4 mt-6">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading || validating}
              className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <LoadingButton
              loading={loading || validating}
              onClick={handleSubmit}
              disabled={!isFormValid()}
              className="px-8 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 border border-transparent rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {actionText}
            </LoadingButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SubjectModal;